// src/pages/ListingDetail.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getListingById, archiveListing, restoreListing } from '../services/ListingsService';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { recordPayment } from '../services/PaymentService';
import MediaCarousel from '../components/MediaCarousel';
import LocationMap from '../components/LocationMap';
import PaymentModal from '../components/PaymentModal';
import './Listings.css';
import './ListingPaymentStyles.css';

function ListingDetail() {
  const { id } = useParams();
  const { currentUser, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [hasPurchased, setHasPurchased] = useState(false);
  const [signupSuccess, setSignupSuccess] = useState(false);
  const [signupError, setSignupError] = useState(null);
  const [isSigningUp, setIsSigningUp] = useState(false);
  const [currentAttendeeCount, setCurrentAttendeeCount] = useState(0);
  const [purchasedQuantity, setPurchasedQuantity] = useState(0);
  const [archiving, setArchiving] = useState(false);
  const [archiveError, setArchiveError] = useState(null);
  
  // For development - check if we're in local development mode
  const isLocalDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  
  
  useEffect(() => {
    const fetchListing = async () => {
      try {
        const data = await getListingById(id);
        setListing(data);
        
        // Check if the current user has already purchased this listing
        if (currentUser && data.purchasers) {
          const alreadyPurchased = data.purchasers.includes(currentUser.uid);
          setHasPurchased(alreadyPurchased);
        }
        
        // Get current attendee count from listing document
        setCurrentAttendeeCount(data.currentAttendees || 0);

        setLoading(false);
      } catch (err) {
        console.error("Error fetching listing:", err);
        setError("Failed to load listing details. Please try again later.");
        setLoading(false);
      }
    };
    
    fetchListing();
  }, [id, currentUser]);

  // Fetch user's purchased quantity for this listing
  useEffect(() => {
    const fetchUserPurchases = async () => {
      if (!currentUser) return;
      try {
        const paymentsQ = query(
          collection(db, 'payments'),
          where('userId', '==', currentUser.uid),
          where('listingId', '==', id),
          where('status', '==', 'succeeded')
        );
        const snap = await getDocs(paymentsQ);
        const totalQty = snap.docs.reduce((sum, d) => sum + (d.data().quantity || 1), 0);
        setPurchasedQuantity(totalQty);
      } catch (e) {
        console.warn('Could not load user purchase quantity:', e);
      }
    };
    fetchUserPurchases();
  }, [id, currentUser]);
  
  const handleArchiveToggle = async () => {
    if (!listing) return;
    const shouldArchive = listing.archived !== true;
    if (!window.confirm(`Are you sure you want to ${shouldArchive ? 'archive' : 'restore'} this listing?`)) {
      return;
    }
    setArchiving(true);
    setArchiveError(null);
    try {
      if (shouldArchive) {
        await archiveListing(id);
      } else {
        await restoreListing(id);
      }
      setListing(prev => ({
        ...prev,
        archived: shouldArchive,
        archivedAt: shouldArchive ? new Date() : null
      }));
    } catch (err) {
      console.error("Error updating archive status:", err);
      setArchiveError("Failed to update listing status. Please try again.");
    } finally {
      setArchiving(false);
    }
  };
  
  const handlePaymentClick = () => {
    if (listing?.archived) {
      alert('This listing is currently archived and cannot accept new purchases.');
      return;
    }
    if (!currentUser) {
      navigate('/signin');
      return;
    }
    
    // Check if listing is at capacity
    if (listing.maxAttendees && currentAttendeeCount >= listing.maxAttendees) {
      setPaymentError('This listing is at full capacity. No more attendees can sign up.');
      return;
    }
    
    setIsPaymentModalOpen(true);
  };
  
  const handlePaymentSuccess = async (paymentDetails) => {
    try {
      // PaymentForm already records the payment; avoid double-recording
      setPaymentSuccess(true);
      setHasPurchased(true);
      setIsPaymentModalOpen(false);

      // Optimistically update attendee count and purchased quantity; webhook will persist it server-side
      const qty = paymentDetails?.quantity || 1;
      setPurchasedQuantity(prev => prev + qty);
      setCurrentAttendeeCount(prev => prev + qty);
    } catch (err) {
      console.error("Post-payment handling error:", err);
      setPaymentError("Payment was successful but there was a follow-up error. Please contact support.");
    }
  };
  
  const handlePaymentError = (error) => {
    setPaymentError(error);
    setIsPaymentModalOpen(false);
  };

  // Handle free signup
  const handleFreeSignup = async () => {
    if (listing?.archived) {
      alert('This listing is currently archived and not accepting signups.');
      return;
    }
    if (!currentUser) {
      navigate('/signin');
      return;
    }
    
    // Check if listing is at capacity
    if (listing.maxAttendees && currentAttendeeCount >= listing.maxAttendees) {
      setSignupError('This listing is at full capacity. No more attendees can sign up.');
      return;
    }

    setIsSigningUp(true);
    setSignupError(null);

    try {
      console.log('Recording free signup for listing:', id);
      // Record the free signup as a $0 payment
      await recordPayment({
        userId: currentUser.uid,
        listingId: id,
        amount: 0,
        paymentIntentId: 'free_signup_' + Date.now(),
        status: 'succeeded',
        listerEmail: listing.contact
      });
      console.log('Free signup recorded successfully');
      
      setSignupSuccess(true);
      setHasPurchased(true);
      
      // Refresh the listing data to get updated attendee count
      setTimeout(async () => {
        try {
          const updatedListing = await getListingById(id);
          setCurrentAttendeeCount(updatedListing.currentAttendees || 0);
          console.log('Updated attendee count:', updatedListing.currentAttendees);
        } catch (error) {
          console.error('Error refreshing listing data:', error);
        }
      }, 1000); // Wait 1 second for the database update to propagate
    } catch (err) {
      console.error("Error recording free signup:", err);
      setSignupError("Failed to sign up. Please try again.");
    }

    setIsSigningUp(false);
  };
  
  // Helper function to format social media URLs
  const formatSocialMediaUrl = (platform, value) => {
    if (!value) return null;
    
    // If it's already a full URL, return as is
    if (value.startsWith('http://') || value.startsWith('https://')) {
      return value;
    }
    
    // Format based on platform
    switch (platform) {
      case 'instagram':
        // Handle @username format
        if (value.startsWith('@')) {
          return `https://www.instagram.com/${value.substring(1)}`;
        }
        return `https://www.instagram.com/${value}`;
        
      case 'twitter':
        // Handle @username format
        if (value.startsWith('@')) {
          return `https://twitter.com/${value.substring(1)}`;
        }
        return `https://twitter.com/${value}`;
        
      default:
        return value;
    }
  };
  
  // Helper function to get social media icon
  const getSocialMediaIcon = (platform) => {
    const icons = {
      website: (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="2" y1="12" x2="22" y2="12"></line>
          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
        </svg>
      ),
      facebook: (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path>
        </svg>
      ),
      instagram: (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
          <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
          <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
        </svg>
      ),
      twitter: (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z"></path>
        </svg>
      ),
      linkedin: (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path>
          <rect x="2" y="9" width="4" height="12"></rect>
          <circle cx="4" cy="4" r="2"></circle>
        </svg>
      ),
      youtube: (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.4 19c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.25 29 29 0 0 0-.46-5.33z"></path>
          <polygon points="9.75 15.02 15.5 11.75 9.75 8.48 9.75 15.02"></polygon>
        </svg>
      )
    };
    return icons[platform] || null;
  };
  
  if (loading) return <div className="listing-detail-container">Loading...</div>;
  if (error) return <div className="listing-detail-container" style={{ color: 'red' }}>{error}</div>;
  if (!listing) return <div className="listing-detail-container">Listing not found.</div>;
  
  const isOwner = currentUser && (currentUser.uid === listing.userId || isAdmin);
  const canViewCapacity = isOwner || isAdmin;
  const isArchived = listing.archived === true;
  
  const priceValue = parseFloat(listing.price || 0);
  const isFree = listing.isFreeEvent || priceValue === 0;
  const isContactForPrice = listing.isContactForPrice;
  const remainingCapacity = listing.maxAttendees ? Math.max(0, listing.maxAttendees - currentAttendeeCount) : null;
  
  // Determine price display
  let priceDisplay = '';
  if (listing.isFreeEvent) {
    priceDisplay = 'Free Event';
  } else if (listing.isContactForPrice) {
    priceDisplay = 'Contact for Price';
  } else if (priceValue > 0) {
    priceDisplay = '$' + priceValue.toFixed(2);
  }
  // If priceValue is 0 and not explicitly marked as free event, show nothing
  
  const locationEntries = !listing.isVirtual
    ? (
        Array.isArray(listing.locations) && listing.locations.length > 0
          ? listing.locations.filter(loc => loc && loc.address)
          : listing.location
            ? [{ id: 'primary', address: listing.location, coordinates: listing.coordinates || null }]
            : []
      )
    : [];
  const primaryCoordinates = locationEntries[0]?.coordinates || listing.coordinates || null;
  const placeholderImage = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300" viewBox="0 0 400 300"%3E%3Crect width="400" height="300" fill="%23e0e0e0"/%3E%3Ctext x="50%25" y="50%25" font-family="Arial, sans-serif" font-size="20" fill="%23999" text-anchor="middle" dy=".3em"%3ENo Image Available%3C/text%3E%3C/svg%3E';
  
  return (
    <div className="listing-detail-container">
      <div className="listing-detail-header">
        <h1>{listing.title}</h1>
        <div className="listing-meta">
          <span>Category: {listing.category}</span>
          {listing.isVirtual && <span>• Virtual Event</span>}
        </div>
        {listing.eventStart && (
          <div className="listing-detail-section">
            <h2>Event Date</h2>
            <p>
              {new Date(listing.eventStart).toLocaleString('en-CA', { dateStyle: 'medium', timeStyle: 'short' })}
              {listing.eventEnd && (
                <>
                  {' '}–{' '}
                  {new Date(listing.eventEnd).toLocaleString('en-CA', { dateStyle: 'medium', timeStyle: 'short' })}
                </>
              )}
            </p>
          </div>
        )}
      </div>

      {isArchived && (
        <div style={{
          backgroundColor: '#fff4e5',
          border: '1px solid #ffd6a5',
          borderRadius: '8px',
          padding: '1rem',
          marginBottom: '1.5rem',
          color: '#8c5404'
        }}>
          <strong>This listing is archived.</strong> It won't appear publicly until it is restored.
        </div>
      )}
      
      {/* Media Carousel or Image */}
      {listing.media && listing.media.length > 0 ? (
        <MediaCarousel 
          media={listing.media} 
          title={listing.title}
        />
      ) : listing.imageUrl ? (
        // Fallback for listings that only have imageUrl (legacy)
        <MediaCarousel 
          media={[{ url: listing.imageUrl, type: 'image' }]} 
          title={listing.title}
        />
      ) : (
        // If no media at all, show placeholder
        <div className="listing-detail-image">
          <img 
            src={placeholderImage} 
            alt="No content available" 
          />
        </div>
      )}
      
      <div className="listing-detail-content">
        {priceDisplay && (
          <div className="listing-detail-price">
            {priceDisplay}
          </div>
        )}
        
        {/* Show attendee count and capacity - only to owner and admin */}
        {canViewCapacity && listing.maxAttendees && (
          <div style={{ 
            marginBottom: '1rem',
            padding: '0.75rem',
            backgroundColor: currentAttendeeCount >= listing.maxAttendees ? '#fef2f2' : '#f0fdf4',
            border: `1px solid ${currentAttendeeCount >= listing.maxAttendees ? '#fecaca' : '#bbf7d0'}`,
            borderRadius: '8px',
            color: currentAttendeeCount >= listing.maxAttendees ? '#dc2626' : '#16a34a'
          }}>
            <strong>Attendees: {currentAttendeeCount} / {listing.maxAttendees}</strong>
            {currentAttendeeCount >= listing.maxAttendees && (
              <div style={{ marginTop: '0.25rem', fontSize: '0.9rem' }}>
                This listing is at full capacity.
              </div>
            )}
          </div>
        )}
        
        {/* Show attendee count for unlimited listings - only to owner and admin */}
        {canViewCapacity && !listing.maxAttendees && currentAttendeeCount > 0 && (
          <div style={{ 
            marginBottom: '1rem',
            padding: '0.75rem',
            backgroundColor: '#f0fdf4',
            border: '1px solid #bbf7d0',
            borderRadius: '8px',
            color: '#16a34a'
          }}>
            <strong>Current Attendees: {currentAttendeeCount}</strong>
          </div>
        )}
        
        {/* Show capacity status to public users - without exact numbers */}
        {!canViewCapacity && listing.maxAttendees && currentAttendeeCount >= listing.maxAttendees && (
          <div style={{ 
            marginBottom: '1rem',
            padding: '0.75rem',
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '8px',
            color: '#dc2626'
          }}>
            <strong>This listing is at full capacity.</strong>
          </div>
        )}
        
        <div className="listing-detail-section">
          <h2>Description</h2>
          <p style={{ whiteSpace: 'pre-wrap' }}>{listing.description}</p>
        </div>
        
        {!listing.isVirtual && (
          <div className="listing-detail-section">
            <h2>{locationEntries.length > 1 ? 'Locations' : 'Location'}</h2>
            {locationEntries.length === 0 ? (
              <p>Location details will be shared soon.</p>
            ) : (
              <ul style={{ margin: '0 0 1rem 1.25rem', padding: 0 }}>
                {locationEntries.map((loc, index) => (
                  <li key={loc.id || `loc-${index}`} style={{ marginBottom: '0.5rem', fontSize: '1rem', color: '#444' }}>
                    {loc.address}
                    {index === 0 && <span style={{ color: '#999', marginLeft: '0.35rem' }}>(Primary)</span>}
                  </li>
                ))}
              </ul>
            )}
            
            {locationEntries.length > 0 && (
              <div style={{ marginTop: '1rem' }}>
                <LocationMap 
                  coordinates={primaryCoordinates} 
                  title={listing.title}
                  isVirtual={listing.isVirtual}
                  address={locationEntries[0].address}
                  locations={locationEntries}
                />
              </div>
            )}
          </div>
        )}
        
        {/* Social Media Section */}
        {listing.socialMedia && Object.values(listing.socialMedia).some(value => value) && (
          <div className="listing-detail-section" style={{ 
            backgroundColor: '#f8f9fa', 
            padding: '1.5rem', 
            borderRadius: '8px',
            marginTop: '2rem'
          }}>
            <h2 style={{ marginTop: 0 }}>Connect With Us</h2>
            <div style={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: '1rem',
              marginTop: '1rem'
            }}>
              {Object.entries(listing.socialMedia).map(([platform, value]) => {
                if (!value) return null;
                const url = formatSocialMediaUrl(platform, value);
                const icon = getSocialMediaIcon(platform);
                
                return (
                  <a
                    key={platform}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.5rem 1rem',
                      backgroundColor: 'white',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      textDecoration: 'none',
                      color: '#333',
                      transition: 'all 0.3s ease',
                      textTransform: 'capitalize'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--color-primary)';
                      e.currentTarget.style.color = 'white';
                      e.currentTarget.style.borderColor = 'var(--color-primary)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'white';
                      e.currentTarget.style.color = '#333';
                      e.currentTarget.style.borderColor = '#ddd';
                    }}
                  >
                    {icon}
                    <span>{platform === 'website' ? 'Website' : platform}</span>
                  </a>
                );
              })}
            </div>
          </div>
        )}
        
        {/* Owner Actions */}
        {isOwner && (
          <div style={{ 
            marginTop: '2rem', 
            padding: '1rem', 
            backgroundColor: '#f8f9fa', 
            borderRadius: '8px' 
          }}>
            <h3>Manage Your Listing</h3>
            {archiveError && (
              <div style={{ color: '#c0392b', marginTop: '0.5rem' }}>{archiveError}</div>
            )}
            <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem', flexWrap: 'wrap' }}>
              <Link 
                to={`/edit-listing/${id}`}
                style={{
                  backgroundColor: '#4a90e2',
                  color: 'white',
                  padding: '0.5rem 1rem',
                  borderRadius: '4px',
                  textDecoration: 'none'
                }}
              >
                Edit Listing
              </Link>
              <button 
                onClick={handleArchiveToggle}
                disabled={archiving}
                style={{
                  backgroundColor: listing.archived ? '#27ae60' : '#f39c12',
                  color: 'white',
                  padding: '0.5rem 1rem',
                  borderRadius: '4px',
                  border: 'none',
                  cursor: archiving ? 'not-allowed' : 'pointer',
                  opacity: archiving ? 0.7 : 1
                }}
              >
                {listing.archived ? 'Restore Listing' : 'Archive Listing'}
              </button>
            </div>
            <p style={{ marginTop: '0.75rem', fontSize: '0.85rem', color: '#666' }}>
              Archiving hides the listing from the marketplace without deleting your media or sales history.
            </p>
          </div>
        )}

        {!isOwner && isArchived && (
          <div className="contact-section">
            <h2>Currently Unavailable</h2>
            <p>This listing has been archived by the provider and is not accepting signups right now.</p>
          </div>
        )}

        {/* Contact Section for Non-Owners */}
        {!isOwner && !isArchived && !isContactForPrice && !isFree && (
          <>
            {paymentError && (
              <div className="payment-error-message">
                {paymentError}
              </div>
            )}
            
            {paymentSuccess ? (
              <div className="payment-section">
                <div className="payment-success-box">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                  <div>
                    <h3>Payment Complete!</h3>
                    <p>You have purchased access to this listing. Contact the provider at: <strong>{listing.contact}</strong></p>
                    <p>Tickets purchased: <strong>{purchasedQuantity}</strong></p>
                  </div>
                </div>
              </div>
            ) : hasPurchased ? (
              <div className="contact-section">
                <h2>Contact Information</h2>
                <p>You have already purchased this listing.</p>
                <p>Tickets purchased: <strong>{purchasedQuantity}</strong></p>
                <p>Email: <strong>{listing.contact}</strong></p>
                {remainingCapacity !== 0 && (
                  <div style={{ marginTop: '1rem' }}>
                    <button onClick={handlePaymentClick} className="payment-button">
                      Purchase More Tickets
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="payment-section">
                <h2>Purchase This Listing</h2>
                <p>Get access to contact information for ${priceValue.toFixed(2)}</p>
                {remainingCapacity != null && (
                  <p style={{ color: '#666' }}>{remainingCapacity} ticket(s) remaining</p>
                )}
                <button onClick={handlePaymentClick} className="payment-button">
                  Purchase Now
                </button>
              </div>
            )}
          </>
        )}
        
        {/* Contact Section for Free Events or Contact for Price */}
        {!isOwner && !isArchived && (isFree || isContactForPrice) && (
          <div className="contact-section">
            {signupError && (
              <div className="payment-error-message">
                {signupError}
              </div>
            )}
            
            {signupSuccess ? (
              <div className="payment-section">
                <div className="payment-success-box">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                  <div>
                    <h3>Signup Complete!</h3>
                    <p>You have signed up for this {isFree ? 'free event' : 'listing'}. Contact the provider at: <strong>{listing.contact}</strong></p>
                  </div>
                </div>
              </div>
            ) : hasPurchased ? (
              <div>
                <h2>Contact Information</h2>
                <p>You have already signed up for this {isFree ? 'free event' : 'listing'}.</p>
                <p>Email: <strong>{listing.contact}</strong></p>
              </div>
            ) : currentUser ? (
              <div>
                {isFree && !isContactForPrice && (
                  <div className="signup-section">
                    <h2>Sign Up for Free Event</h2>
                    <p>Sign up to get contact information for this free event.</p>
                    <button 
                      onClick={handleFreeSignup} 
                      className="payment-button"
                      disabled={isSigningUp}
                    >
                      {isSigningUp ? 'Signing Up...' : 'Sign Up Now'}
                    </button>
                  </div>
                )}
                {isContactForPrice && (
                  <div>
                    <h2>Contact Information</h2>
                    <p>Email: <strong>{listing.contact}</strong></p>
                  </div>
                )}
              </div>
            ) : (
              <div className="auth-prompt">
                <h3>Sign in to {isFree && !isContactForPrice ? 'sign up' : 'view contact information'}</h3>
                <p>You need to be signed in to {isFree && !isContactForPrice ? 'sign up for this free event' : 'contact the listing owner'}.</p>
                <Link to="/signin" className="signin-btn">
                  Sign In
                </Link>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Payment Modal */}
      {isPaymentModalOpen && (
        <PaymentModal
          isOpen={isPaymentModalOpen}
          onClose={() => setIsPaymentModalOpen(false)}
          listingId={id}
          price={priceValue}
          userId={currentUser ? currentUser.uid : null}
          listerEmail={listing.contact}
          remainingCapacity={remainingCapacity}
          onPaymentSuccess={handlePaymentSuccess}
          onPaymentError={handlePaymentError}
        />
      )}
      
      {/* Development Notice */}
      {isLocalDevelopment && (
        <div style={{ 
          marginTop: '2rem', 
          padding: '1rem', 
          backgroundColor: '#fff3cd', 
          border: '1px solid #ffeeba',
          borderRadius: '4px',
          color: '#856404'
        }}>
          <strong>Development Mode:</strong> Payment processing is simulated. No actual charges will be made.
        </div>
      )}
    </div>
  );
}

export default ListingDetail;
